/**
 * Tarjeta de una solicitud de unión + hook con las acciones de aprobar/rechazar.
 *
 * Se comparte entre las dos pantallas que procesan solicitudes:
 *  - `JoinRequestsPage`            → buzón del club (director).
 *  - `SectionDirectorRequestsPage` → buzón de la asociación, solo las que
 *                                    piden DIRIGIR un tipo de club.
 *
 * Ambas aprueban por la misma Cloud Function, así que el aviso de "solicita
 * dirigir" y el consentimiento explícito (`grantSectionDirector`) viven aquí y
 * no pueden quedar desincronizados entre pantallas.
 */

import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import type { JoinRequest } from "@/lib/services/joinRequests";
import { formatDate } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

export const SECTION_LABELS: Record<string, string> = {
  aventureros: "Aventureros",
  conquistadores: "Conquistadores",
  guias_mayores: "Guías Mayores",
};

/** ¿La solicitud pide dirigir un tipo de club (y no solo entrar)? */
export function isSectionDirectorRequest(request: JoinRequest): boolean {
  return request.requestedRole === "section_director";
}

// ============================================
// ACCIONES (aprobar / rechazar)
// ============================================

export interface JoinRequestActions {
  processingId: string | null;
  error: string | null;
  notice: string | null;
  setError: (msg: string | null) => void;
  approve: (request: JoinRequest) => Promise<void>;
  reject: (request: JoinRequest, reason?: string) => Promise<void>;
}

/**
 * Aprobar/rechazar vía Cloud Function compartida con la app.
 *
 * La CF crea/reutiliza Person, crea/reactiva Member y marca la solicitud en una
 * transacción; si además pide dirigir una sección, asigna el rol. La
 * autorización real la hace ella (director general o admin de asociación para
 * las de dirección): esta capa solo pinta el resultado.
 */
export function useJoinRequestActions(): JoinRequestActions {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const approve = async (request: JoinRequest) => {
    setProcessingId(request.id);
    setError(null);
    setNotice(null);

    try {
      const approveFn = httpsCallable<
        { requestId: string; grantSectionDirector?: boolean },
        {
          ok: boolean;
          sectionDirector?: {
            assigned: boolean;
            section?: string;
            reason?: string;
          };
          // F12-P1: la ficha pertenece a otra cuenta — la membresía se creó,
          // pero quien solicitó no la verá hasta que se traslade la ficha.
          linkConflict?: { attached: false; maskedEmail?: string };
        }
      >(functions, "approveJoinRequest");
      const res = await approveFn({
        requestId: request.id,
        // Consentimiento explícito: estas pantallas SÍ muestran qué sección
        // pide dirigir, así que pueden autorizar la concesión. La CF la rechaza
        // si el cliente no lo manda (p.ej. la app móvil, que aún no lo muestra).
        grantSectionDirector: isSectionDirectorRequest(request),
      });

      // Si pedía dirigir una sección, informar el resultado: la membresía se
      // aprueba igual, pero la asignación puede fallar (sección ya con
      // director, sin cuenta vinculada, menor de 16…).
      const sd = res.data?.sectionDirector;
      if (sd?.assigned) {
        setNotice(
          `Aprobado. ${request.firstName ?? request.userName} quedó como director/a de ${
            SECTION_LABELS[sd.section ?? ""] ?? sd.section
          }.`,
        );
      } else if (sd && !sd.assigned) {
        setError(
          `Se aprobó la membresía, pero NO quedó como director/a de ${
            SECTION_LABELS[sd.section ?? ""] ?? sd.section ?? "ese tipo de club"
          }: ${sd.reason ?? "motivo desconocido"}`,
        );
      }

      // F12-P1: antes esto pasaba en silencio y la persona quedaba reenviando
      // solicitudes sin entender por qué no veía el club.
      const lc = res.data?.linkConflict;
      if (lc) {
        setError(
          `Aprobado, PERO ${request.firstName ?? request.userName} no verá el club ` +
            `al entrar: su ficha está asociada a otra cuenta` +
            `${lc.maskedEmail ? ` (${lc.maskedEmail})` : ""}. ` +
            `Dile que entre con ese correo, o pide el traslado de su ficha a soporte.`,
        );
      }
    } catch (err) {
      console.error("Error approving request:", err);
      const msg = err instanceof Error ? err.message : "Error desconocido";
      setError(`Error al aprobar la solicitud: ${msg}`);
    } finally {
      setProcessingId(null);
    }
  };

  const reject = async (request: JoinRequest, reason?: string) => {
    setProcessingId(request.id);
    setError(null);

    try {
      const rejectFn = httpsCallable<
        { requestId: string; reason?: string },
        { ok: boolean }
      >(functions, "rejectJoinRequest");
      await rejectFn({ requestId: request.id, reason: reason || undefined });
    } catch (err) {
      console.error("Error rejecting request:", err);
      setError("Error al rechazar la solicitud");
    } finally {
      setProcessingId(null);
    }
  };

  return { processingId, error, notice, setError, approve, reject };
}

// ============================================
// TARJETA
// ============================================

export default function JoinRequestCard({
  request,
  processingId,
  onApprove,
  onReject,
  showClub = false,
}: {
  request: JoinRequest;
  processingId: string | null;
  onApprove: (request: JoinRequest) => void;
  onReject: (request: JoinRequest, reason?: string) => void;
  /**
   * Mostrar a qué club pertenece la solicitud. El director ya lo sabe; el
   * admin de asociación ve solicitudes de muchos clubes a la vez.
   */
  showClub?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const hasPersonalInfo =
    request.firstName || request.phone || request.birthDate;

  const toDate = (d: unknown): Date | undefined => {
    if (!d) return undefined;
    if (d instanceof Date) return d;
    if (typeof d === "object" && d !== null && "toDate" in d) {
      return (d as { toDate: () => Date }).toDate();
    }
    if (typeof d === "object" && d !== null && "seconds" in d) {
      return new Date((d as { seconds: number }).seconds * 1000);
    }
    return undefined;
  };

  const formatDate_local = (date?: unknown) => {
    const d = toDate(date);
    if (!d) return "No especificada";
    return formatDate(d, "d 'de' MMMM 'de' yyyy");
  };

  const calculateAge = (birthDate?: unknown) => {
    const bd = toDate(birthDate);
    if (!bd) return null;
    const today = new Date();
    let age = today.getFullYear() - bd.getFullYear();
    const monthDiff = today.getMonth() - bd.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bd.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(request.birthDate);
  const isMinor = age !== null && age < 18;

  const documentTypeLabel = (type?: string) => {
    const labels: Record<string, string> = {
      cc: "C.C.",
      ti: "T.I.",
      ce: "C.E.",
      passport: "Pasaporte",
      rc: "R.C.",
    };
    return labels[type?.toLowerCase() || ""] || type || "";
  };

  const isProcessing = processingId === request.id;
  const fullName =
    request.firstName && request.lastName
      ? `${request.firstName} ${request.lastName}`
      : request.userName;

  return (
    <div className="bg-white rounded-xl border-0 shadow-md overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="flex-shrink-0 mt-0.5">
            {request.userPhotoUrl ? (
              <img
                src={request.userPhotoUrl}
                alt={fullName}
                className="h-11 w-11 rounded-full object-cover ring-2 ring-white dark:ring-gray-700 shadow-sm"
              />
            ) : (
              <div className="h-11 w-11 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-sm ring-2 ring-white dark:ring-gray-700">
                <span className="text-base font-bold text-white">
                  {(request.firstName || request.userName)
                    .charAt(0)
                    .toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Name + timestamp row */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 leading-tight truncate">
                  {fullName}
                </h3>
                <p className="text-sm text-gray-500 truncate mt-0.5">
                  {request.userEmail}
                </p>
              </div>
              <span className="flex-shrink-0 text-xs text-gray-400 mt-0.5 whitespace-nowrap">
                {formatDistanceToNow(request.createdAt, {
                  addSuffix: true,
                  locale: es,
                })}
              </span>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
              {showClub && request.clubName && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                  {request.clubName}
                </span>
              )}
              {age !== null && (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${
                    isMinor
                      ? "bg-amber-50 text-amber-700 ring-amber-200"
                      : "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  }`}
                >
                  {age} años{isMinor && " · menor"}
                </span>
              )}
              {request.idDocumentNumber && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
                  {documentTypeLabel(request.idDocumentType)}{" "}
                  {request.idDocumentNumber}
                </span>
              )}
            </div>

            {/* Pide dirigir un tipo de club: el aprobador tiene que verlo sí o sí */}
            {isSectionDirectorRequest(request) && (
              <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50 px-3.5 py-2.5">
                <p className="text-sm font-semibold text-primary-900">
                  Solicita dirigir{" "}
                  {SECTION_LABELS[request.requestedSection ?? ""] ??
                    request.requestedSection}
                  {showClub && request.clubName ? ` · ${request.clubName}` : ""}
                </p>
                <p className="text-xs text-primary-700 mt-0.5">
                  Al aprobar, además de quedar como miembro queda como
                  director/a de ese tipo de club (y se abre en el club si aún no
                  estaba). Verifica que de verdad sea quien lo dirige.
                </p>
              </div>
            )}

            {/* Message */}
            {request.message && (
              <div className="mt-3 text-sm text-gray-600 bg-gray-50 rounded-xl px-3.5 py-2.5 border border-gray-100">
                <span className="italic">&ldquo;{request.message}&rdquo;</span>
              </div>
            )}
          </div>
        </div>

        {/* Divider + actions */}
        <div className="mt-4 pt-3.5 border-t border-gray-100 flex items-center justify-between">
          {hasPersonalInfo ? (
            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors"
            >
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              {expanded ? "Ocultar detalles" : "Ver información personal"}
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            {rejectMode ? (
              <div className="flex items-center gap-2 w-full">
                <input
                  autoFocus
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Motivo del rechazo (opcional)"
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onReject(request, rejectReason || undefined);
                      setRejectMode(false);
                    }
                    if (e.key === "Escape") {
                      setRejectMode(false);
                      setRejectReason("");
                    }
                  }}
                />
                <button
                  onClick={() => {
                    onReject(request, rejectReason || undefined);
                    setRejectMode(false);
                  }}
                  disabled={isProcessing}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40 transition-colors"
                >
                  Confirmar
                </button>
                <button
                  onClick={() => {
                    setRejectMode(false);
                    setRejectReason("");
                  }}
                  className="px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setRejectMode(true)}
                  disabled={isProcessing}
                  aria-label={`Rechazar solicitud de ${fullName}`}
                  className="px-3.5 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-40 border border-transparent hover:border-red-100 dark:hover:border-red-900/40"
                >
                  Rechazar
                </button>
                <button
                  onClick={() => {
                    if (!confirmingApprove) {
                      setConfirmingApprove(true);
                    } else {
                      onApprove(request);
                      setConfirmingApprove(false);
                    }
                  }}
                  disabled={isProcessing}
                  aria-label={
                    confirmingApprove
                      ? `Confirmar aprobación de ${fullName}`
                      : `Aprobar solicitud de ${fullName}`
                  }
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-lg shadow-sm disabled:opacity-40 transition-all ${
                    confirmingApprove
                      ? "text-white bg-green-600 hover:bg-green-700 shadow-green-500/20"
                      : "text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 shadow-primary-500/20"
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <svg
                        className="animate-spin h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Procesando…
                    </>
                  ) : confirmingApprove ? (
                    <>
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      ¿Confirmar?
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Aprobar
                    </>
                  )}
                </button>
                {confirmingApprove && !isProcessing && (
                  <button
                    onClick={() => setConfirmingApprove(false)}
                    className="px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Expanded personal details */}
      {expanded && hasPersonalInfo && (
        <div className="border-t border-gray-100 bg-gradient-to-r from-gray-50/50 to-primary-50/30 px-5 py-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
            Datos personales
          </h4>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            {request.phone && (
              <div>
                <dt className="text-xs text-gray-500">Teléfono</dt>
                <dd className="mt-0.5 font-medium text-gray-900">
                  {request.phone}
                </dd>
              </div>
            )}
            {request.birthDate && (
              <div>
                <dt className="text-xs text-gray-500">Nacimiento</dt>
                <dd className="mt-0.5 font-medium text-gray-900">
                  {formatDate_local(request.birthDate)}
                </dd>
              </div>
            )}
            {request.gender && (
              <div>
                <dt className="text-xs text-gray-500">Género</dt>
                <dd className="mt-0.5 font-medium text-gray-900">
                  {request.gender === "male" ? "Masculino" : "Femenino"}
                </dd>
              </div>
            )}
            {request.bloodType && (
              <div>
                <dt className="text-xs text-gray-500">Sangre</dt>
                <dd className="mt-0.5 font-medium text-gray-900">
                  {request.bloodType}
                </dd>
              </div>
            )}
            {request.address && (
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs text-gray-500">Dirección</dt>
                <dd className="mt-0.5 font-medium text-gray-900">
                  {request.address}
                </dd>
              </div>
            )}
          </dl>

          {request.emergencyContact && (
            <div className="mt-4 pt-3.5 border-t border-gray-200">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                Contacto de emergencia{isMinor && " · Acudiente"}
              </h4>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs text-gray-500">Nombre</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {request.emergencyContact.name}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Parentesco</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {request.emergencyContact.relationship}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500">Teléfono</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {request.emergencyContact.phone}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
